import React, { useState } from 'react'
import { TextInput } from '../common/text-input'
import {
  AddIcon,
  SearchActiveIcon,
  SearchBtnIcon,
  SearchIcon
} from '@/components/agent-dashboard/assets';
import { useTranslation } from 'react-i18next';
import { Dropdown } from '@/components/ui/modal';
import { cn } from '@/lib/utils';
import { TextButton } from '../common/text-button';
import { CreateOrderModal } from './create-order-modal';
import { SearchFormData } from '@/manager/manager/agent-dashboard-order-manager';
import { AddOrderIcon } from './assets';
import { OrderSeatData } from '@/services/agent-v2-order-seat-list';
import { useToast } from '@/hooks/use-toast';
import { ExpirationVerify } from '../agent-dashboard/expiration-verify';

interface SearchBarProps {
  searchFormData: SearchFormData,
  search: (page: number) => void,
  changeSearchForm: (key: keyof SearchFormData, value: SearchFormData[keyof SearchFormData]) => void,
  resetAndSearch: () => void,
  /** 搜索订单类型下拉项  */
  orderSeatListForSearch?: OrderSeatData[],
  /** 创建订单弹窗的商品信息下拉项  */
  orderSeatListForCreate?: OrderSeatData[],
}

/** 智能体订单管理-搜索栏 */
export const SearchBar: React.FC<SearchBarProps> = ({
  searchFormData,
  search,
  changeSearchForm,
  resetAndSearch,
  orderSeatListForSearch,
  orderSeatListForCreate,
}) => {
  const { t } = useTranslation();
  const [openCreateOrderModal, setOpenCreateOrderModal] = useState(false);
  const { toast } = useToast();
  const handleSearch = () => {
    search(1);
  }

  const handleReset = () => {
    resetAndSearch();
  }

  const handleOpenCreateOrderModal = () => {
    if (!orderSeatListForCreate?.length) {
      toast.default({
        description: t('agent_dashboard.order_management.create_modal.no_goods_tips'),
      });
      return;
    }
    setOpenCreateOrderModal(true);
  }

  return (
    <div className='flex justify-between items-end'>
      <div className='flex flex-wrap gap-3'>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.order_management.search.conditional_filtering')}</label>
          <Dropdown
            value={searchFormData.searchType}
            placeholder={t('agent_dashboard.order_management.search.please_select')}
            className='h-9'
            onValueChange={(value) => changeSearchForm('searchType', value)}
            width='11.3rem'
            options={[
              { value: 'order_no', label: t('agent_dashboard.order_management.search.filter.order_no') },
              { value: 'user_name', label: t('agent_dashboard.order_management.search.filter.name') },
              { value: 'user_id', label: t('agent_dashboard.order_management.search.filter.user_id') },
            ]}
          />
        </div>
        <div className='flex items-end'>
          <TextInput
            value={searchFormData.searchValue}
            placeholder={t('agent_dashboard.order_management.search.search_placeholder')}
            className='w-[25rem] h-9 flex-1 font-normal'
            prefixIcon={<SearchIcon className='w-5 h-5' />}
            prefixActiveIcon={<SearchActiveIcon className='w-5 h-5' />}
            onChange={(e) => changeSearchForm('searchValue', e.target.value)}
          />
        </div>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>
            {t('agent_dashboard.order_management.search.order_type')}
          </label>
          <Dropdown
            value={searchFormData.goodsId?.toString() || ''}
            placeholder={t('agent_dashboard.order_management.search.select_placeholder')}
            className='h-9 font-medium'
            onValueChange={(value) => changeSearchForm('goodsId', value)}
            width='11rem'
            options={orderSeatListForSearch?.map((seat) => ({
              value: seat.id,
              label: seat.name,
            }))}
          />
        </div>
        <div className='flex items-end space-x-3'>
          <TextButton
            label={t('agent_dashboard.order_management.search.reset')}
            className={cn(
              'max-w-max !px-5 h-9 bg-white',
              'font-semibold text-sm !text-[rgba(65,70,81,1)]',
              'border border-[rgba(213, 215, 218, 1)] hover:bg-gray-50',
            )}
            onClick={handleReset}
          />
          <TextButton
            label={t('agent_dashboard.order_management.search.search')}
            icon={<SearchBtnIcon className='w-5 h-5' />}
            className={cn(
              '!w-[6.4rem] h-9 ',
              'text-white font-semibold text-sm',
            )}
            onClick={handleSearch}
          />
        </div>
      </div>
      <div>
        <ExpirationVerify>
          <TextButton
            icon={<AddOrderIcon />}
            label={t('agent_dashboard.order_management.create_order')}
            className={cn(
              'max-w-max h-9',
              'text-white font-semibold text-sm',
            )}
            onClick={handleOpenCreateOrderModal}
          />
        </ExpirationVerify>
        {openCreateOrderModal && (
          <CreateOrderModal
            open={openCreateOrderModal}
            onOpenChange={setOpenCreateOrderModal}
            orderSeatList={orderSeatListForCreate}
          />
        )}
      </div>
    </div>
  )
}
